/**
 * PDF header template registry (client mirror).
 * Canonical copy: account_api/worker/src/pdf/header/templates.js
 */

import { computeDueDate, formatDueDate } from "../dueDates";
import { formatInvoiceDate, formatInvoiceNumber, todayIso } from "../invoiceFormat";

export type PdfTemplateId = "classic";

export interface MetaLineSpec {
  text: string;
  size: number;
  bold?: boolean;
}

export interface HeaderTemplateSpec {
  id: PdfTemplateId;
  /** Logo column width as fraction of content width. */
  slotFraction: number;
  minHeaderHeightPt: number;
  dividerGapPt: number;
  dividerHeightPt: number;
  /** Gap between page top and first meta line (0 when meta is bottom-aligned). */
  metaTopInsetPt: number;
  /** Equal inset on all four sides of the logo placement box (preview + PDF). */
  logoInsetLeftPt: number;
  logoInsetRightPt: number;
  logoInsetTopPt: number;
  logoInsetBottomPt: number;
  /** Equal inset on all four sides of the meta placement box (preview + PDF). */
  metaInsetLeftPt: number;
  metaInsetRightPt: number;
  metaInsetTopPt: number;
  metaInsetBottomPt: number;
}

export const HEADER_TEMPLATES: Record<PdfTemplateId, HeaderTemplateSpec> = {
  classic: {
    id: "classic",
    slotFraction: 0.6,
    minHeaderHeightPt: 48,
    dividerGapPt: 6,
    dividerHeightPt: 2,
    metaTopInsetPt: 0,
    logoInsetLeftPt: 4,
    logoInsetRightPt: 4,
    logoInsetTopPt: 4,
    logoInsetBottomPt: 4,
    metaInsetLeftPt: 4,
    metaInsetRightPt: 4,
    metaInsetTopPt: 4,
    metaInsetBottomPt: 4,
  },
};

const DEFAULT_TEMPLATE_ID: PdfTemplateId = "classic";

export function resolvePdfTemplateId(business: Record<string, unknown> | undefined): PdfTemplateId {
  const id = String(business?.pdf_template || DEFAULT_TEMPLATE_ID).toLowerCase();
  return id in HEADER_TEMPLATES ? (id as PdfTemplateId) : DEFAULT_TEMPLATE_ID;
}

export function getHeaderTemplateSpec(templateId: PdfTemplateId = DEFAULT_TEMPLATE_ID): HeaderTemplateSpec {
  return HEADER_TEMPLATES[templateId in HEADER_TEMPLATES ? templateId : DEFAULT_TEMPLATE_ID];
}

/** Meta lines for logo editor preview using the selected business profile. */
export function buildClassicPreviewMetaLines(business: Record<string, unknown>): MetaLineSpec[] {
  const gstRegistered = business.gst_registered !== false;
  const title = gstRegistered ? "Tax Invoice" : "Invoice";
  const invNum = formatInvoiceNumber(Number(business.invoice_counter) || 1);
  const dateIso = todayIso();
  const dateFmt = formatInvoiceDate(dateIso);
  const due = computeDueDate(
    dateIso,
    business.due_rule_type,
    business.due_net_days,
    business.due_fixed_date
  );
  const dueFmt = formatDueDate(due);
  return [
    { text: title, size: 18, bold: true },
    { text: `Invoice # ${invNum}`, size: 10, bold: false },
    { text: `Date ${dateFmt}`, size: 10, bold: false },
    { text: `Due ${dueFmt}`, size: 10, bold: false },
  ];
}
